import { Avatar, Stack, Typography } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { Badge, FlagBelarus, FlagPoland, InlineEnglishIcon, ShapesIcon } from 'framework/ui/mui-builder';
import type { BadgeProps } from 'framework/ui/mui-builder';
import { emptyValue } from '../../shared/constants';
import type { ApplicationDetails, CandidateTag } from '../../shared/types';

type CandidateSectionProps = {
  application: ApplicationDetails;
};

const COUNTRY_FLAGS: Record<string, React.FC<SvgIconProps>> = {
  Poland: FlagPoland,
  Belarus: FlagBelarus,
};

const attributeIconSx = { fontSize: 20, color: 'text.secondary' };

type CandidateAttribute = {
  id: string;
  icon: React.ReactNode;
  value: string;
  isOptional?: boolean;
};

const hasValue = (value: string) => Boolean(value) && value !== emptyValue;

const TAG_BADGE_COLORS: Record<CandidateTag, BadgeProps['color']> = {
  Student: 'success',
  KIV: 'warning',
  Referral: 'info',
  Blacklist: 'error',
  Hidden: 'default',
  Duplicate: 'default',
  Converted: 'primary',
};

const CandidateSection = ({ application }: CandidateSectionProps) => {
  const attributes: CandidateAttribute[] = [
    { id: 'specialization', icon: <ShapesIcon sx={attributeIconSx} />, value: application.specialization },
    {
      id: 'technology',
      icon: <UnfoldMoreIcon sx={{ ...attributeIconSx, transform: 'rotate(90deg)' }} />,
      value: application.technology,
    },
    { id: 'level', icon: <SignalCellularAltIcon sx={attributeIconSx} />, value: application.level, isOptional: true },
    { id: 'language', icon: <LanguageIcon sx={attributeIconSx} />, value: application.language },
    { id: 'languageLevel', icon: <InlineEnglishIcon sx={attributeIconSx} />, value: application.languageLevel },
  ].filter(({ value, isOptional }) => !isOptional || hasValue(value));

  const Flag = COUNTRY_FLAGS[application.country];

  return (
    <Stack direction="row" gap={1.25} sx={{ py: 1.5 }}>
      <Avatar sx={{ width: 72, height: 72 }} />

      <Stack gap={1} flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
          <Typography variant="h6" color="brand">
            {application.candidateName}
          </Typography>

          {application.tags.map(tag => (
            <Badge key={tag} sx={{ fontSize: 10 }} color={TAG_BADGE_COLORS[tag]}>{`# ${tag}`}</Badge>
          ))}

          {Flag && <Flag sx={{ ml: 'auto', fontSize: 24 }} />}
        </Stack>

        <Stack direction="row" flexWrap="wrap" rowGap={1}>
          {attributes.map(attribute => (
            <Stack key={attribute.id} direction="row" alignItems="center" gap={0.5} sx={{ width: '33.33%' }}>
              {attribute.icon}
              <Typography variant="body1" noWrap>
                {attribute.value || emptyValue}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CandidateSection;
